import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import {
  RefreshCw, CheckCircle, XCircle, UserCheck, ShieldCheck, RotateCcw,
  Images, ExternalLink, ChevronDown, ChevronLeft, ChevronRight, Layers, Loader2, Trash2,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { listRiders } from '../../api/orders';
import {
  listReturnRequests,
  approveReturnRequest,
  rejectReturnRequest,
  disposeReturnRequest,
  assignReturnRider,
  completeStoreVerification,
  RETURN_STATUS,
  RETURN_TYPE,
  RETURN_STATUS_LABELS,
  RETURN_RESOLUTION,
  RETURN_ERRORS,
  ORDER_CHANNEL,
  ORDER_CHANNEL_LABELS,
} from '../../api/returns';
import {
  GROUP_STATUS_MIXED,
  groupReturnsByOrderId,
  itemsAssignableForRider,
} from '../../utils/returnGroups';
import styles from './ReturnsQueue.module.css';

function statusLabel(status) {
  if (status === GROUP_STATUS_MIXED) return 'Mixed statuses';
  return RETURN_STATUS_LABELS[status] || status;
}

function statusClass(status) {
  if (status === GROUP_STATUS_MIXED) return styles.statusMIXED;
  return styles[`status${status}`] || '';
}

function pickupProofUrls(req) {
  if (req.podPhotoUrls?.length) return req.podPhotoUrls;
  return req.podPhotoUrl ? [req.podPhotoUrl] : [];
}

function bulkOrderIds(data) {
  return [...new Set(
    data.reduce((acc, r) => {
      const oid = r.orderId || r.id;
      acc.push(oid);
      return acc;
    }, []),
  )].filter((oid) => data.filter((r) => (r.orderId || r.id) === oid).length > 1);
}

const RIDER_ASSIGNABLE_STATUSES = [
  RETURN_STATUS.APPROVED,
  RETURN_STATUS.PICKUP_SCHEDULED,
  RETURN_STATUS.PICKUP_FAILED,
];

const PAGE_SIZE = 10;

export default function ReturnsQueue() {
  const { user } = useAuth();
  const { notify, refreshReturnsBadge } = useOrders();
  const [requests, setRequests] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL');
  const [channelTab, setChannelTab] = useState('ALL');
  const [page, setPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState(() => new Set());
  const pageTopRef = useRef(null);

  const [activeModal, setActiveModal] = useState(null);
  const [selectedReq, setSelectedReq] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [disposeNotes, setDisposeNotes] = useState('');
  const [selectedRiderSub, setSelectedRiderSub] = useState('');
  const [restockById, setRestockById] = useState({});
  const [verificationNotes, setVerificationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Token refresh rotates idToken; keep latest values in refs so the load
  // effect only re-runs on tab change or sign-in (user.sub), not every refresh.
  const tokenRef = useRef(user?.idToken);
  tokenRef.current = user?.idToken;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const orderGroups = useMemo(() => groupReturnsByOrderId(requests), [requests]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, channelTab]);

  const totalPages = Math.max(1, Math.ceil(orderGroups.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedGroups = orderGroups.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = orderGroups.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, orderGroups.length);

  const goToPage = (nextPage) => {
    setPage(nextPage);
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const fetchRequests = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = (await listReturnRequests(activeTab, tokenRef.current, {
        channel: channelTab,
      })).filter(Boolean);
      setRequests(data);
      // Only reset expand state on a full reload (tab change / manual refresh).
      // Silent post-action reloads should keep the cashier's open rows.
      if (!silent) setExpandedOrders(new Set(bulkOrderIds(data)));
    } catch (err) {
      if (!silent) notifyRef.current('Failed to load return requests');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeTab, channelTab]);

  const fetchActiveRiders = useCallback(async () => {
    try {
      const r = await listRiders(tokenRef.current);
      setRiders(r);
      if (r.length > 0) setSelectedRiderSub(r[0].id);
    } catch (err) {
      // Ignore rider load failure
    }
  }, []);

  useEffect(() => {
    if (!user?.sub) return undefined;
    let cancelled = false;
    (async () => {
      await fetchRequests();
      if (!cancelled) await fetchActiveRiders();
    })();
    return () => { cancelled = true; };
  }, [fetchRequests, fetchActiveRiders, user?.sub]);

  const toggleExpanded = (orderId) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const openItemModal = (modal, req) => {
    setSelectedItems([req]);
    setSelectedReq(req);
    setAssignOrderId(null);
    if (modal === 'verify') {
      setRestockById({ [req.id]: true });
    }
    setActiveModal(modal);
  };

  const closeModal = (options = {}) => {
    if (submitting && !options.force) return;
    setActiveModal(null);
    setSelectedReq(null);
    setSelectedItems([]);
    setAssignOrderId(null);
    setRejectReason('');
    setApproveNotes('');
    setDisposeNotes('');
    setVerificationNotes('');
    setRestockById({});
  };

  const openOrderAssignModal = (group) => {
    const assignable = itemsAssignableForRider(group.items, RIDER_ASSIGNABLE_STATUSES);
    if (assignable.length === 0) return;
    setSelectedItems(assignable);
    setSelectedReq(assignable[0]);
    setAssignOrderId(group.orderId);
    setActiveModal('assign');
  };

  const handleApprove = async (req) => {
    openItemModal('approve', req);
    setApproveNotes('');
  };

  const handleDispose = (req) => {
    openItemModal('dispose', req);
    setDisposeNotes('');
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    const req = selectedItems[0] || selectedReq;
    if (!req) return;
    setSubmitting(true);
    try {
      await approveReturnRequest(
        req.id,
        { notes: approveNotes.trim() || undefined },
        user?.idToken,
      );
      notify(`Request #${req.id} approved — assign a rider for pickup`);
      closeModal({ force: true });
      refreshReturnsBadge();
      fetchRequests({ silent: true });
    } catch (err) {
      notify(err.message || 'Failed to approve request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisposeSubmit = async (e) => {
    e.preventDefault();
    const req = selectedItems[0] || selectedReq;
    if (!req) return;
    setSubmitting(true);
    try {
      const updated = await disposeReturnRequest(
        req.id,
        { notes: disposeNotes.trim() || undefined },
        user?.idToken,
      );
      const credited = updated?.creditAmount ?? req.creditAmount ?? 0;
      notify(
        credited > 0
          ? `Request #${req.id}: dispose & credit — ₹${credited} credited, customer discards item`
          : `Request #${req.id}: disposed, but credit_amount is ₹0 — wallet was not topped up. Check the return with BE.`,
      );
      closeModal({ force: true });
      refreshReturnsBadge();
      fetchRequests({ silent: true });
    } catch (err) {
      const code = err?.code || err?.message;
      notify(RETURN_ERRORS[code] || err.message || 'Failed to dispose request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim() || selectedItems.length === 0) return;
    const req = selectedItems[0];
    setSubmitting(true);
    try {
      await rejectReturnRequest(req.id, rejectReason.trim(), user?.idToken);
      notify(`Request #${req.id} rejected`);
      closeModal({ force: true });
      refreshReturnsBadge();
      fetchRequests({ silent: true });
    } catch (err) {
      notify(err.message || 'Failed to reject request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRiderSub || selectedItems.length === 0) return;
    const rider = riders.find((r) => r.id === selectedRiderSub);
    setSubmitting(true);
    try {
      await Promise.all(
        selectedItems.map((req) => assignReturnRider(
          req.id,
          selectedRiderSub,
          rider?.name || 'Rider',
          user?.idToken,
        )),
      );
      const label = assignOrderId && selectedItems.length > 1
        ? `Order ${assignOrderId} (${selectedItems.length} items)`
        : `request #${selectedItems[0].id}`;
      notify(`Rider assigned to ${label}`);
      closeModal({ force: true });
      refreshReturnsBadge();
      fetchRequests({ silent: true });
    } catch (err) {
      notify(err.message || 'Failed to assign rider');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) return;
    const req = selectedItems[0];
    const restock = restockById[req.id] ?? true;
    const notes = verificationNotes;

    // Optimistic UI: close modal immediately so user isn't blocked by browser
    // connection-queue / CORS OPTIONS delays (API itself is ~50ms).
    setRequests((prev) => prev
      .map((r) => (r.id === req.id
        ? { ...r, status: RETURN_STATUS.COMPLETED, verificationNotes: notes }
        : r))
      .filter((r) => activeTab === 'ALL' || r.status === activeTab));
    closeModal({ force: true });
    notify(`Request #${req.id} verified & completed`);

    try {
      await completeStoreVerification(
        req.id,
        { restock, notes },
        user?.idToken,
      );
      fetchRequests({ silent: true });
    } catch (err) {
      notify(err.message || 'Failed to verify request');
      fetchRequests({ silent: true });
    }
  };

  const renderActions = (req) => {
    const canApprove = req.status === RETURN_STATUS.REQUESTED;
    const canDispose = (
      (req.status === RETURN_STATUS.REQUESTED || req.status === RETURN_STATUS.APPROVED)
      && req.type === RETURN_TYPE.RETURN_CREDIT
      && Array.isArray(req.evidencePhotoUrls)
      && req.evidencePhotoUrls.length > 0
      && !req.deliveryPersonSub
    );
    const canAssign = (
      req.status === RETURN_STATUS.APPROVED
      || req.status === RETURN_STATUS.PICKUP_SCHEDULED
      || req.status === RETURN_STATUS.PICKUP_FAILED
    );
    const canVerify = req.status === RETURN_STATUS.PICKUP_COMPLETED;
    const isFailed = req.status === RETURN_STATUS.PICKUP_FAILED;

    return (
      <div className={styles.actionBtnGroup}>
        {canApprove && (
          <>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.approveBtn}`}
              onClick={() => handleApprove(req)}
              title="Approve Return Request"
            >
              <CheckCircle size={14} />
              <span>Approve</span>
            </button>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.rejectBtn}`}
              onClick={() => openItemModal('reject', req)}
              title="Reject Request"
            >
              <XCircle size={14} />
              <span>Reject</span>
            </button>
          </>
        )}
        {canDispose && (
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.disposeBtn}`}
            onClick={() => handleDispose(req)}
            title="Dispose & credit — customer discards item, no pickup"
          >
            <Trash2 size={14} />
            <span className={styles.actionLabelFull}>Dispose & credit</span>
            <span className={styles.actionLabelShort}>Dispose</span>
          </button>
        )}
        {canAssign && (
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.assignBtn}`}
            onClick={() => openItemModal('assign', req)}
            title={isFailed ? 'Retry Pickup with Another Rider' : 'Assign Delivery Rider for Pickup'}
          >
            <UserCheck size={14} />
            <span>
              {isFailed ? 'Retry Pickup' : req.deliveryPersonSub ? 'Reassign Rider' : 'Assign Rider'}
            </span>
          </button>
        )}
        {canVerify && (
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.verifyBtn}`}
            onClick={() => openItemModal('verify', req)}
            title="In-Store Inspection & Verification"
          >
            <ShieldCheck size={14} />
            <span>Verify & Restock</span>
          </button>
        )}
      </div>
    );
  };

  const renderGroupAssignAction = (group) => {
    const assignable = itemsAssignableForRider(group.items, RIDER_ASSIGNABLE_STATUSES);
    if (assignable.length === 0) {
      return <span className={styles.itemMeta}>Approve items below to assign rider</span>;
    }
    const hasFailed = assignable.some((i) => i.status === RETURN_STATUS.PICKUP_FAILED);
    const hasRider = assignable.every((i) => i.deliveryPersonSub);

    return (
      <div className={styles.actionBtnGroup}>
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.assignBtn}`}
          onClick={() => openOrderAssignModal(group)}
          title={hasFailed ? 'Retry pickup for order' : 'Assign one rider for all items in this order'}
        >
          <UserCheck size={14} />
          <span>
            {hasFailed ? 'Retry Pickup' : hasRider ? 'Reassign Rider' : 'Assign Rider'}
            {' '}({assignable.length})
          </span>
        </button>
      </div>
    );
  };

  const renderStatusCell = (req) => (
    <>
      <span className={`${styles.statusChip} ${statusClass(req.status)}`}>
        {RETURN_STATUS_LABELS[req.status] || req.status}
      </span>
      {req.status === RETURN_STATUS.COMPLETED
        && req.resolution === RETURN_RESOLUTION.CUSTOMER_DISPOSE && (
        <div className={styles.disposeChip} style={{ marginTop: '3px' }}>
          {req.creditAmount > 0
            ? `Discard the item. ₹${req.creditAmount} credited`
            : 'Disposed — discard item (₹0 on return; no wallet credit)'}
        </div>
      )}
      {req.deliveryPersonName && (
        <div className={styles.itemMeta} style={{ marginTop: '3px' }}>Rider: {req.deliveryPersonName}</div>
      )}
      {req.status === RETURN_STATUS.COMPLETED && req.type === RETURN_TYPE.EXCHANGE && req.replacementOrderId && (
        <div className={styles.itemMeta} style={{ marginTop: '3px' }}>
          Replacement: <span className={styles.requestId} style={{ fontSize: '12px' }}>{req.replacementOrderId}</span>
        </div>
      )}
      {req.status === RETURN_STATUS.REJECTED && req.rejectionReason && (
        <div className={styles.itemMeta} style={{ marginTop: '3px' }}>{req.rejectionReason}</div>
      )}
      {req.status === RETURN_STATUS.PICKUP_FAILED && req.rejectionReason && (
        <div className={styles.itemMeta} style={{ marginTop: '3px' }}>{req.rejectionReason}</div>
      )}
    </>
  );

  const renderReasonCell = (req) => {
    const proofUrls = pickupProofUrls(req);
    const photoCount = req.evidencePhotoUrls.length + proofUrls.length;
    return (
    <div className={styles.itemCell}>
      <span className={styles.productName} style={{ fontSize: '12.5px' }}>{req.reasonLabel}</span>
      {req.comments && <span className={styles.itemMeta}>{req.comments}</span>}
      {photoCount > 0 && (
        <button
          type="button"
          className={styles.photosLink}
          onClick={() => {
            setSelectedReq(req);
            setSelectedItems([]);
            setActiveModal('photos');
          }}
        >
          <Images size={12} />
          <span>
            View Photos ({photoCount})
          </span>
        </button>
      )}
    </div>
    );
  };

  const renderItemRow = (req, { isChild = false } = {}) => (
    <tr key={req.id} className={isChild ? styles.childRow : undefined}>
      <td>
        <div className={`${styles.requestCell} ${isChild ? styles.childRequestCell : ''}`}>
          <span className={styles.requestId}>
            {req.id}
            {req.orderChannel === ORDER_CHANNEL.SUBS && (
              <span className={styles.channelBadgeSubs}>
                {ORDER_CHANNEL_LABELS[ORDER_CHANNEL.SUBS]}
              </span>
            )}
          </span>
          {!isChild && (
            <span className={styles.orderRef}>Order: {req.orderId}</span>
          )}
        </div>
      </td>
      {!isChild && (
        <td>
          <div className={styles.customerCell}>
            <span className={styles.customerName}>{req.customerName}</span>
            <span className={styles.customerPhone}>{req.customerPhone}</span>
          </div>
        </td>
      )}
      {isChild && <td />}
      <td>
        <div className={styles.itemCell}>
          <span className={styles.productName}>{req.productName}</span>
          <span className={styles.itemMeta}>
            {req.type === RETURN_TYPE.EXCHANGE
              ? `Qty: ${req.qty} · Exchange`
              : req.creditAmount > 0
                ? `Qty: ${req.qty} · ₹${req.unitPrice} each (Credit ₹${req.creditAmount})`
                : `Qty: ${req.qty} · Store credit`}
          </span>
        </div>
      </td>
      <td>
        <span className={`${styles.typeChip} ${req.type === RETURN_TYPE.RETURN_CREDIT ? styles.typeReturn : styles.typeExchange}`}>
          <RotateCcw size={13} />
          {req.type === RETURN_TYPE.RETURN_CREDIT ? 'Store Credit' : 'Exchange'}
        </span>
      </td>
      <td>{renderReasonCell(req)}</td>
      <td>{renderStatusCell(req)}</td>
      <td>{renderActions(req)}</td>
    </tr>
  );

  const renderMobileItemCard = (req, { nested = false } = {}) => (
    <article key={req.id} className={`${styles.mobileCard} ${nested ? styles.mobileCardNested : ''}`}>
      <div className={styles.mobileCardTop}>
        <div className={styles.requestCell}>
          <span className={styles.requestId}>
            {req.id}
            {req.orderChannel === ORDER_CHANNEL.SUBS && (
              <span className={styles.channelBadgeSubs}>
                {ORDER_CHANNEL_LABELS[ORDER_CHANNEL.SUBS]}
              </span>
            )}
          </span>
          {!nested && <span className={styles.orderRef}>Order: {req.orderId}</span>}
        </div>
        <span className={`${styles.typeChip} ${req.type === RETURN_TYPE.RETURN_CREDIT ? styles.typeReturn : styles.typeExchange}`}>
          <RotateCcw size={13} />
          {req.type === RETURN_TYPE.RETURN_CREDIT ? 'Store Credit' : 'Exchange'}
        </span>
      </div>

      {!nested && (
        <div className={styles.customerCell}>
          <span className={styles.customerName}>{req.customerName}</span>
          <span className={styles.customerPhone}>{req.customerPhone}</span>
        </div>
      )}

      <div className={styles.itemCell}>
        <span className={styles.productName}>{req.productName}</span>
        <span className={styles.itemMeta}>
          {req.type === RETURN_TYPE.EXCHANGE
            ? `Qty: ${req.qty} · Exchange`
            : req.creditAmount > 0
              ? `Qty: ${req.qty} · ₹${req.unitPrice} each (Credit ₹${req.creditAmount})`
              : `Qty: ${req.qty} · Store credit`}
        </span>
      </div>

      {renderReasonCell(req)}
      <div className={styles.mobileStatus}>{renderStatusCell(req)}</div>
      <div className={styles.mobileActions}>{renderActions(req)}</div>
    </article>
  );

  const renderMobileGroup = (group) => {
    const expanded = expandedOrders.has(group.orderId);
    const returnCount = group.items.filter((i) => i.type === RETURN_TYPE.RETURN_CREDIT).length;
    const exchangeCount = group.items.length - returnCount;

    return (
      <div key={group.orderId} className={styles.mobileGroup}>
        <article className={`${styles.mobileCard} ${styles.mobileGroupCard}`}>
          <button
            type="button"
            className={styles.mobileExpandBtn}
            onClick={() => toggleExpanded(group.orderId)}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <div className={styles.requestCell}>
              <span className={styles.requestId}>
                {group.orderId}
                {group.items.some((i) => i.orderChannel === ORDER_CHANNEL.SUBS) && (
                  <span className={styles.channelBadgeSubs}>
                    {ORDER_CHANNEL_LABELS[ORDER_CHANNEL.SUBS]}
                  </span>
                )}
              </span>
              <span className={styles.bulkBadge}>
                <Layers size={11} />
                {group.items.length} items
              </span>
            </div>
          </button>

          <div className={styles.customerCell}>
            <span className={styles.customerName}>{group.customerName}</span>
            <span className={styles.customerPhone}>{group.customerPhone}</span>
          </div>

          <div className={styles.itemCell}>
            <span className={styles.productName}>
              {group.items.length} return/exchange items
            </span>
            <span className={styles.itemMeta}>
              {group.totalQty} units · Total ₹{group.totalCredit}
            </span>
            <span className={styles.itemMeta}>
              {group.items.map((i) => i.productName).slice(0, 2).join(', ')}
              {group.items.length > 2 ? ` +${group.items.length - 2} more` : ''}
            </span>
          </div>

          <div className={styles.typeStack}>
            {returnCount > 0 && (
              <span className={`${styles.typeChip} ${styles.typeReturn}`}>
                {returnCount} Store Credit
              </span>
            )}
            {exchangeCount > 0 && (
              <span className={`${styles.typeChip} ${styles.typeExchange}`}>
                {exchangeCount} Exchange
              </span>
            )}
          </div>

          <div className={styles.mobileStatus}>
            <span className={`${styles.statusChip} ${statusClass(group.status)}`}>
              {statusLabel(group.status)}
            </span>
            {group.deliveryPersonName && group.status !== GROUP_STATUS_MIXED && (
              <div className={styles.itemMeta} style={{ marginTop: '3px' }}>
                Rider: {group.deliveryPersonName}
              </div>
            )}
          </div>

          <div className={styles.mobileActions}>{renderGroupAssignAction(group)}</div>
        </article>

        {expanded && (
          <div className={styles.mobileNestedList}>
            {group.items.map((req) => renderMobileItemCard(req, { nested: true }))}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'ALL', label: 'All Requests' },
    { key: RETURN_STATUS.REQUESTED, label: 'Pending Approval' },
    { key: RETURN_STATUS.APPROVED, label: 'Approved / Ready' },
    { key: RETURN_STATUS.PICKUP_SCHEDULED, label: 'Pickup Scheduled' },
    { key: RETURN_STATUS.PICKUP_COMPLETED, label: 'In-Store Verification' },
    { key: RETURN_STATUS.COMPLETED, label: 'Completed' },
  ];

  const channelTabs = [
    { key: 'ALL', label: 'All channels' },
    { key: ORDER_CHANNEL.DELIVERY, label: ORDER_CHANNEL_LABELS[ORDER_CHANNEL.DELIVERY] },
    { key: ORDER_CHANNEL.SUBS, label: ORDER_CHANNEL_LABELS[ORDER_CHANNEL.SUBS] },
  ];

  const selectedReqForModal = selectedItems[0] || selectedReq;

  return (
    <div ref={pageTopRef}>
      <PageHeader
        eyebrow="Operations"
        title="Return & Exchange Queue"
        subtitle="Manage customer item return requests, credit issuance, rider pickups, and inventory verification."
        actions={(
          <Button
            variant="outline"
            icon={RefreshCw}
            type="button"
            onClick={() => fetchRequests()}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
      />

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`${styles.filterTab} ${activeTab === t.key ? styles.filterTabActive : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.channelFilterRow}>
        <span className={styles.channelFilterLabel}>Channel</span>
        <div className={styles.channelTabs}>
          {channelTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`${styles.channelTab} ${channelTab === t.key ? styles.channelTabActive : ''}`}
              onClick={() => setChannelTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.listArea}>
        {loading ? (
          <div className={styles.emptyState}>Loading return requests...</div>
        ) : requests.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No return requests found</h3>
            <p>
              {channelTab === ORDER_CHANNEL.SUBS
                ? 'No subscription return/exchange requests match this filter. After Support → Require return (with the new API deployed), look under Approved / Ready. Older “Require return” tickets only updated Support — reopen that ticket and confirm Require return again.'
                : 'There are currently no item return or exchange requests matching this filter.'}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Request ID / Order</th>
                    <th>Customer</th>
                    <th>Item & Details</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedGroups.map((group) => {
                    if (!group.isBulk) {
                      return renderItemRow(group.items[0]);
                    }

                    const expanded = expandedOrders.has(group.orderId);
                    const returnCount = group.items.filter((i) => i.type === RETURN_TYPE.RETURN_CREDIT).length;
                    const exchangeCount = group.items.length - returnCount;

                    return (
                      <Fragment key={group.orderId}>
                        <tr className={styles.groupRow}>
                          <td>
                            <button
                              type="button"
                              className={styles.expandBtn}
                              onClick={() => toggleExpanded(group.orderId)}
                              aria-expanded={expanded}
                            >
                              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              <div className={styles.requestCell}>
                                <span className={styles.requestId}>
                                  {group.orderId}
                                  {group.items.some((i) => i.orderChannel === ORDER_CHANNEL.SUBS) && (
                                    <span className={styles.channelBadgeSubs}>
                                      {ORDER_CHANNEL_LABELS[ORDER_CHANNEL.SUBS]}
                                    </span>
                                  )}
                                </span>
                                <span className={styles.bulkBadge}>
                                  <Layers size={11} />
                                  {group.items.length} items
                                </span>
                              </div>
                            </button>
                          </td>
                          <td>
                            <div className={styles.customerCell}>
                              <span className={styles.customerName}>{group.customerName}</span>
                              <span className={styles.customerPhone}>{group.customerPhone}</span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.itemCell}>
                              <span className={styles.productName}>
                                {group.items.length} return/exchange items
                              </span>
                              <span className={styles.itemMeta}>
                                {group.totalQty} units · Total ₹{group.totalCredit}
                              </span>
                              <span className={styles.itemMeta}>
                                {group.items.map((i) => i.productName).slice(0, 2).join(', ')}
                                {group.items.length > 2 ? ` +${group.items.length - 2} more` : ''}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.typeStack}>
                              {returnCount > 0 && (
                                <span className={`${styles.typeChip} ${styles.typeReturn}`}>
                                  {returnCount} Store Credit
                                </span>
                              )}
                              {exchangeCount > 0 && (
                                <span className={`${styles.typeChip} ${styles.typeExchange}`}>
                                  {exchangeCount} Exchange
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={styles.itemMeta}>Expand to view per-item reasons</span>
                          </td>
                          <td>
                            <span className={`${styles.statusChip} ${statusClass(group.status)}`}>
                              {statusLabel(group.status)}
                            </span>
                            {group.deliveryPersonName && group.status !== GROUP_STATUS_MIXED && (
                              <div className={styles.itemMeta} style={{ marginTop: '3px' }}>
                                Rider: {group.deliveryPersonName}
                              </div>
                            )}
                          </td>
                          <td>{renderGroupAssignAction(group)}</td>
                        </tr>
                        {expanded && group.items.map((req) => renderItemRow(req, { isChild: true }))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.mobileList} aria-label="Return requests">
              {pagedGroups.map((group) => (
                group.isBulk
                  ? renderMobileGroup(group)
                  : renderMobileItemCard(group.items[0])
              ))}
            </div>

            <div className={styles.paginationBar}>
              <span className={styles.paginationSummary}>
                Showing {rangeStart}–{rangeEnd} of {orderGroups.length} request{orderGroups.length === 1 ? '' : 's'}
              </span>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() => goToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className={styles.paginationPage}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {activeModal === 'approve' && selectedReqForModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Approve #{selectedReqForModal.id}</h2>
                <p className={styles.modalSubtitle}>{selectedReqForModal.productName}</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleApproveSubmit}>
              <div className={styles.modalBody}>
                <p className={styles.modalHint}>
                  {selectedReqForModal.type === RETURN_TYPE.EXCHANGE
                    ? 'Approving schedules rider pickup. A free replacement order is created only when Ops verifies after pickup.'
                    : selectedReqForModal.creditAmount > 0
                      ? `Approving schedules rider pickup. ₹${selectedReqForModal.creditAmount} wallet credit is issued on verify after pickup, or via Dispose & credit when photos are enough.`
                      : 'Approving schedules rider pickup. Wallet credit is issued on verify after pickup, or via Dispose & credit when photos are enough.'}
                </p>
                <div className={styles.formGroup}>
                  <label>Note (optional)</label>
                  <textarea
                    className={styles.formControl}
                    rows={2}
                    placeholder="Internal note…"
                    value={approveNotes}
                    onChange={(e) => setApproveNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className={`${styles.actionBtn} ${styles.approveBtn}`} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Saving…</span>
                    </>
                  ) : (
                    'Approve for pickup'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === 'dispose' && selectedReqForModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Dispose &amp; credit #{selectedReqForModal.id}</h2>
                <p className={styles.modalSubtitle}>{selectedReqForModal.productName}</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleDisposeSubmit}>
              <div className={styles.modalBody}>
                <p className={styles.modalHint}>
                  Customer discards the item — no rider pickup.
                  {selectedReqForModal.creditAmount > 0
                    ? ` Credits ₹${selectedReqForModal.creditAmount} to their wallet now (photos already on the request).`
                    : ' Warning: this return has credit_amount ₹0 — dispose will complete but will not add wallet credit. Ask BE why unit_price/credit_amount were not set on create.'}
                </p>
                <div className={styles.formGroup}>
                  <label>Note (optional)</label>
                  <textarea
                    className={styles.formControl}
                    rows={2}
                    placeholder="e.g. Spoilt milk — photos confirm leak…"
                    value={disposeNotes}
                    onChange={(e) => setDisposeNotes(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className={`${styles.actionBtn} ${styles.disposeBtn}`} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Crediting…</span>
                    </>
                  ) : selectedReqForModal.creditAmount > 0 ? (
                    `Dispose & credit ₹${selectedReqForModal.creditAmount}`
                  ) : (
                    'Dispose & credit'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === 'reject' && selectedReqForModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Reject Return Request #{selectedReqForModal.id}</h2>
                <p className={styles.modalSubtitle}>{selectedReqForModal.productName}</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleRejectSubmit}>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label>Reason for Rejection</label>
                  <textarea
                    className={styles.formControl}
                    rows={3}
                    placeholder="Provide reason for rejection..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className={`${styles.actionBtn} ${styles.approveBtn}`} style={{ background: '#e11d48' }} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Rejecting…</span>
                    </>
                  ) : (
                    'Reject Request'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === 'assign' && selectedReqForModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>
                  {assignOrderId && selectedItems.length > 1
                    ? `Assign Rider — Order ${assignOrderId}`
                    : `Assign Rider for Request #${selectedReqForModal.id}`}
                </h2>
                <p className={styles.modalSubtitle}>
                  {assignOrderId && selectedItems.length > 1
                    ? `${selectedItems.length} items in this order`
                    : selectedReqForModal.productName}
                </p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleAssignSubmit}>
              <div className={styles.modalBody}>
                {assignOrderId && selectedItems.length > 1 && (
                  <ul className={styles.bulkItemList}>
                    {selectedItems.map((req) => (
                      <li key={req.id}>{req.id} — {req.productName} (Qty {req.qty})</li>
                    ))}
                  </ul>
                )}
                <div className={styles.formGroup}>
                  <label>Select Delivery Agent</label>
                  <select
                    className={styles.formControl}
                    value={selectedRiderSub}
                    onChange={(e) => setSelectedRiderSub(e.target.value)}
                    required
                  >
                    {riders.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.phone || r.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className={`${styles.actionBtn} ${styles.assignBtn}`} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Assigning rider…</span>
                    </>
                  ) : (
                    'Assign Rider'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === 'verify' && selectedReqForModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={`${styles.modalCard} ${submitting ? styles.modalCardBusy : ''}`} onClick={(e) => e.stopPropagation()}>
            {submitting && (
              <div className={styles.modalBusyOverlay} aria-live="polite" aria-busy="true">
                <Loader2 size={28} className={styles.spin} />
                <span>Completing return &amp; restock…</span>
              </div>
            )}
            <div className={styles.modalHeader}>
              <div>
                <h2>In-Store Verification #{selectedReqForModal.id}</h2>
                <p className={styles.modalSubtitle}>{selectedReqForModal.productName}</p>
              </div>
              <button type="button" className={styles.modalClose} onClick={closeModal} disabled={submitting} aria-label="Close">✕</button>
            </div>
            <form onSubmit={handleVerifySubmit}>
              <div className={styles.modalBody}>
                {(selectedReqForModal.evidencePhotoUrls.length > 0 || pickupProofUrls(selectedReqForModal).length > 0) && (
                  <div className={styles.formGroup}>
                    <label>Evidence & Pickup Photos</label>
                    <div className={styles.photoGrid}>
                      {selectedReqForModal.evidencePhotoUrls.map((url, index) => (
                        <a key={`evidence-${index}`} href={url} target="_blank" rel="noreferrer" className={styles.photoThumbWrap}>
                          <img src={url} alt="Customer evidence" className={styles.photoThumb} />
                          <span className={styles.photoThumbLabel}>Evidence</span>
                        </a>
                      ))}
                      {pickupProofUrls(selectedReqForModal).map((url, index) => (
                        <a key={`pickup-${index}`} href={url} target="_blank" rel="noreferrer" className={styles.photoThumbWrap}>
                          <img src={url} alt={`Pickup proof ${index + 1}`} className={styles.photoThumb} />
                          <span className={styles.photoThumbLabel}>
                            Pickup Proof{pickupProofUrls(selectedReqForModal).length > 1 ? ` ${index + 1}` : ''}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={restockById[selectedReqForModal.id] ?? true}
                    onChange={(e) => setRestockById({ [selectedReqForModal.id]: e.target.checked })}
                    disabled={submitting}
                  />
                  <span>Restock {selectedReqForModal.qty} unit(s) of &apos;{selectedReqForModal.productName}&apos;</span>
                </label>
                <div className={styles.formGroup}>
                  <label>Store Verification Notes (Optional)</label>
                  <textarea
                    className={styles.formControl}
                    rows={2}
                    placeholder="Notes regarding item condition upon arrival..."
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className={`${styles.actionBtn} ${styles.verifyBtn}`} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Completing return…</span>
                    </>
                  ) : (
                    'Complete Return & Restock'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === 'photos' && selectedReq && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Photos — #{selectedReq.id}</h2>
              <button type="button" className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {selectedReq.evidencePhotoUrls.length > 0 && (
                <div className={styles.formGroup}>
                  <label>Customer Evidence</label>
                  <div className={styles.photoGrid}>
                    {selectedReq.evidencePhotoUrls.map((url, index) => (
                      <a key={`evidence-${index}`} href={url} target="_blank" rel="noreferrer" className={styles.photoThumbWrap}>
                        <img src={url} alt="Customer evidence" className={styles.photoThumb} />
                        <span className={styles.photoThumbLabel}>
                          <ExternalLink size={10} /> Open
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {pickupProofUrls(selectedReq).length > 0 && (
                <div className={styles.formGroup}>
                  <label>Rider Pickup Proof</label>
                  <div className={styles.photoGrid}>
                    {pickupProofUrls(selectedReq).map((url, index) => (
                      <a key={`pickup-${index}`} href={url} target="_blank" rel="noreferrer" className={styles.photoThumbWrap}>
                        <img src={url} alt={`Pickup proof ${index + 1}`} className={styles.photoThumb} />
                        <span className={styles.photoThumbLabel}>
                          <ExternalLink size={10} /> Open{pickupProofUrls(selectedReq).length > 1 ? ` (${index + 1})` : ''}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={`${styles.actionBtn} ${styles.rejectBtn}`} onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
